import { Calendar, CalendarEvent } from '@demind-inc/core';
import { getCalendarEnergyZoneBlockStyle } from '../../Energy/energyZoneStyles';
import { RBCEvent } from './types';
import { getCssVariable } from '../../../utils/styles/styles';
import {
  isAISuggestedEvent,
  isTemporalAiSuggestedTaskEvent,
  isTemporalAutoScheduledEvent,
  isTemporalEventId,
} from '../../../helpers';
import dayjs from 'dayjs';
import type { ChatSuggestedEvent } from '../../../data-access';

const primaryColor = getCssVariable('--color-primary') || '#680ddb';
export const ROUNDED_UP_MINUTES_RANGE = 15;

const getCurrentUserResponseStatus = (event: CalendarEvent, targetCalendar?: Calendar) => {
  const meAttendee = event.attendees?.find((attendee) =>
    [targetCalendar?.internalGCalendarId, targetCalendar?.rootEmail].includes(attendee.email)
  );

  return meAttendee?.responseStatus;
};

export const transformEventToRBCEvent = (
  event: CalendarEvent,
  targetCalendar?: Calendar
): RBCEvent => {
  const responseStatus = getCurrentUserResponseStatus(event, targetCalendar);
  const isDeclined = responseStatus === 'declined';
  const isAwaitingResponse = responseStatus === 'needsAction';

  return {
    ...event,
    title: event.summary,
    start: new Date(event.start.date),
    end: new Date(event.end.date),
    allDay: event.isAllDay,
    calendarColor: targetCalendar?.color!,
    isDeclined,
    isAwaitingResponse,
  };
};

export const transformAllDayEventToRBCEvent = (
  event: CalendarEvent,
  targetCalendar?: Calendar
): RBCEvent => {
  const responseStatus = getCurrentUserResponseStatus(event, targetCalendar);
  const isDeclined = responseStatus === 'declined';
  const isAwaitingResponse = responseStatus === 'needsAction';

  return {
    ...event,
    title: event.summary,
    start: new Date(event.start.date.substring(0, 19)), // Convert 2025-04-03T00:00:00.000Z -> 2025-04-03T00:00:00 so start time is 00:00
    end: new Date(event.end.date.substring(0, 19)),
    allDay: event.isAllDay,
    calendarColor: targetCalendar?.color!,
    isDeclined,
    isAwaitingResponse,
  };
};

export const transformCreatingEventToRBCEvent = (event: Partial<CalendarEvent>): RBCEvent => {
  return {
    ...event,
    ...(event.start ? { start: new Date(event.start.date) } : {}),
    ...(event.end ? { end: new Date(event.end.date) } : {}),
  } as RBCEvent;
};

export const transformRBCEventToCalendarEvent = (event: RBCEvent): CalendarEvent => {
  const { isTemporalSuggested: _isTemporalSuggested, ...baseEvent } = event;

  return {
    ...baseEvent,
    summary: baseEvent.title,
    isAllDay: baseEvent.allDay,
    start: { date: baseEvent.start.toISOString(), timeZone: dayjs.tz.guess() },
    end: { date: baseEvent.end.toISOString(), timeZone: dayjs.tz.guess() },
    description: baseEvent.description,
  } as CalendarEvent;
};

export const transfomrAutoScheduledEvents = <
  T extends CalendarEvent & Partial<Pick<RBCEvent, 'isReplanPreview'>>,
>(
  events: T[],
  calendarColor: string = ''
): RBCEvent[] => {
  return events.map((event) => ({
    ...event,
    start: new Date(event.start.date),
    end: new Date(event.end.date),
    title: event.summary,
    allDay: event.isAllDay,
    calendarColor,
  }));
};

export const transformActivityEventsToRBCEvents = (events: CalendarEvent[]): RBCEvent[] => {
  return events.map((event) => {
    return {
      ...event,
      start: new Date(event.start.date),
      end: new Date(event.end.date),
      allDay: event.isAllDay,
      title: event.summary,
      color: event.color || primaryColor,
    };
  });
};

export const getEventStyles = (
  rbcEvent: RBCEvent,
  borderHorizontal = false,
  isWeekView = false
) => {
  const baseStyles = { style: { padding: 0 } };

  // Background event (energy zone bands)
  if (!rbcEvent.eventId) {
    const zoneColor = rbcEvent.color || primaryColor;

    if (!borderHorizontal) {
      return {
        style: getCalendarEnergyZoneBlockStyle(zoneColor),
      };
    }

    return {
      style: {
        backgroundColor: `${zoneColor}18`,
        border: 'none',
        borderRadius: '0',
        color: `${zoneColor}80`,
      },
    };
  }

  const isCreatingEvent = rbcEvent.eventId === 'creating';
  const isTemporalAiScheduler = isTemporalAutoScheduledEvent(rbcEvent.eventId, ['aiScheduler']);
  const isOtherAutoScheduledEvent = isTemporalAutoScheduledEvent(rbcEvent.eventId, [
    'activity',
    'energyBoost',
  ]);

  const borderRadius = isWeekView ? '8px' : '8px';

  // Temporal aiScheduler / replan previews: EventBlock owns the suggested chrome.
  // Week view skips infinite glow here — dozens of concurrent box-shadow
  // animations make plan-my-week feel laggy; EventBlock uses a static style.
  if (
    rbcEvent.isReplanPreview === true ||
    isTemporalAiScheduler ||
    (rbcEvent.autoScheduleFrom?.from === 'aiScheduler' && rbcEvent.isTemporalSuggested)
  ) {
    return {
      style: {
        ...baseStyles.style,
        backgroundColor: 'transparent',
        border: 'none',
        borderRadius,
        zIndex: isWeekView ? 20 : 100,
        ...(isWeekView
          ? {}
          : {
              // Day view: keep a light wrapper cue; EventBlock still paints the pulse.
              // Avoid duplicating suggested-border-glow on both layers.
            }),
      },
    };
  }

  if (isOtherAutoScheduledEvent) {
    return {
      style: {
        ...baseStyles.style,
        backgroundColor: `${primaryColor}4D`,
        border: `1px dashed ${primaryColor}`,
        ...(isWeekView ? {} : { backdropFilter: 'blur(15px)' }),
      },
    };
  }

  if (rbcEvent.isAwaitingResponse) {
    const accentColor = rbcEvent.color || rbcEvent.calendarColor || primaryColor;

    return {
      style: {
        ...baseStyles.style,
        backgroundColor: 'rgba(255, 255, 255, 0.96)',
        border: `1.5px solid ${accentColor}`,
        borderRadius,
        boxShadow: 'none',
      },
    };
  }

  if (isAISuggestedEvent(rbcEvent.autoScheduleFrom)) {
    const isAiSuggetedTemporalTaskEvent = isTemporalAiSuggestedTaskEvent(rbcEvent);

    // Temporal task suggestion events: let EventBlock handle all visual styling
    if (isAiSuggetedTemporalTaskEvent) {
      return {
        style: {
          ...baseStyles.style,
          backgroundColor: 'transparent',
          border: `1px solid rgba(104, 13, 219, 0.7)`,
          borderRadius,
          animation: 'suggested-border-glow 2.2s ease-in-out infinite',
          zIndex: 100,
        },
      };
    }

    return {
      style: {
        ...baseStyles.style,
        backgroundColor: 'transparent',
        border: 'none',
      },
    };
  }

  return {
    style: {
      ...baseStyles.style,
      backgroundColor: isCreatingEvent ? 'white' : 'transparent',
      border: isCreatingEvent ? `solid 2px ${primaryColor}` : 'none',
      borderRadius: '8px',
    },
  };
};

/**
 * Returns a rounded up date and time based on the given date.
 * @param date - The date to round up.
 * @returns The rounded up date and time.
 */
export const getRoundUpDateTime = (
  date: dayjs.Dayjs,
  roundingMinutes = ROUNDED_UP_MINUTES_RANGE
) => {
  const minutes = date.get('minutes');
  const remainder = minutes % roundingMinutes;

  // If already at a 15-minute boundary, don't round up
  if (remainder === 0) {
    return date.startOf('minutes');
  }

  return date.add(roundingMinutes - remainder, 'minutes').startOf('minutes');
};

export const getRoundDownDateTime = (date: dayjs.Dayjs) => {
  return date
    .subtract(date.get('minutes') % ROUNDED_UP_MINUTES_RANGE, 'minutes')
    .startOf('minutes');
};

/**
 * Transforms chat suggested events to RBC events.
 * @param suggestions - The chat suggested events.
 * @returns The RBC events.
 */

export const transformChatSuggesteBlockToRBCEvents = (
  suggestions: ChatSuggestedEvent[]
): RBCEvent[] => {
  return suggestions.map((s) => ({
    eventId: s.eventId,
    title: s.name,
    start: new Date(s.startTime),
    end: new Date(s.endTime),
    summary: s.description!,
    autoScheduleFrom: s.autoScheduleFrom,
    description: s.description,
    taskFrom: s.taskFrom,
    updatedDate: dayjs().toISOString(),
    createdDate: dayjs().toISOString(),
    isTemporalSuggested: true,
  }));
};

const getTimeValue = (value?: Date | string) => {
  if (!value) {
    return null;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.valueOf() : null;
};

const isMealCalendarEvent = (event: RBCEvent) =>
  event.scheduleFrom === 'meal' || event.autoScheduleFrom?.from === 'meal';

const getMealLabel = (event: RBCEvent) => `${event.title || ''}`.toLowerCase();

/**
 * Resolves event IDs that should be hidden when temporary chat suggestions are shown.
 * Includes id-based overrides and meal "update" replacements that are identified by `before` slots.
 */
export const getOverridingEventIds = (
  calendarEvents: RBCEvent[],
  temporalSuggestedEvents: ChatSuggestedEvent[]
) => {
  // Prefer before.id (real calendar event id on result_json updates) over
  // action_id-keyed eventId — matches lifestack_calendar CalendarView.
  const overridingEventIds = new Set(
    temporalSuggestedEvents
      .filter((event) => event.action === 'setEvent' && !!event.before)
      .map((event) => event.before?.id || event.eventId)
      .filter((id): id is string => !!id)
  );

  temporalSuggestedEvents.forEach((event) => {
    if (event.action !== 'setEvent' || !event.before || event.autoScheduleFrom?.from !== 'meal') {
      return;
    }

    const beforeStart = getTimeValue(event.before.startTime);
    const beforeEnd = getTimeValue(event.before.endTime);
    if (beforeStart === null || beforeEnd === null) {
      return;
    }

    const targetMealType = (
      event.before.mealType ||
      event.autoScheduleFrom?.id ||
      ''
    ).toLowerCase();

    calendarEvents.forEach((calendarEvent) => {
      if (!calendarEvent.eventId || overridingEventIds.has(calendarEvent.eventId)) {
        return;
      }
      if (!isMealCalendarEvent(calendarEvent)) {
        return;
      }

      const eventStart = getTimeValue(calendarEvent.start);
      const eventEnd = getTimeValue(calendarEvent.end);
      if (eventStart !== beforeStart || eventEnd !== beforeEnd) {
        return;
      }

      if (targetMealType && !getMealLabel(calendarEvent).includes(targetMealType)) {
        return;
      }

      overridingEventIds.add(calendarEvent.eventId);
    });
  });

  return overridingEventIds;
};
